import type { FC, ReactNode } from "react";

import { isActiveStatus } from "./statusColors";

/* ------------------------------------------------------------------ */
/*  StatusDot                                                          */
/* ------------------------------------------------------------------ */

export interface StatusDotProps {
  /** Tailwind `bg-{color}` class (e.g. from `getStatusDot`). */
  color: string;
  /** Pulse animation for running / in-progress states. */
  pulse?: boolean;
}

/** Tiny colored square used as a status indicator inside accent cards. */
export const StatusDot: FC<StatusDotProps> = ({ color, pulse }) => (
  <span
    className={`h-1.5 w-1.5 flex-shrink-0 ${color} ${pulse ? "animate-pulse" : ""}`}
  />
);

/* ------------------------------------------------------------------ */
/*  AccentCard                                                         */
/* ------------------------------------------------------------------ */

export interface AccentCardProps {
  /** Tailwind `border-l-{color}` class (e.g. from `getStatusAccent`). */
  accent?: string;
  /** Raw status string — used to auto-derive `pulse` for StatusDots inside. */
  status?: string | null;
  /** Click handler. When provided the card renders as a `<button>`. */
  onClick?: () => void;
  /** Disable the card (only meaningful when `onClick` is set). */
  disabled?: boolean;
  /** Native tooltip. */
  title?: string;
  /**
   * Visual variant:
   * - `"default"` — white bg, standard border (top-level cards)
   * - `"muted"` — subtle bg, lighter border (nested/secondary cards)
   */
  variant?: "default" | "muted";
  /** Extra Tailwind classes merged onto the root element. */
  className?: string;
  children: ReactNode;
}

const BASE = "rounded-none border border-l-[3px] text-left transition-colors";
const INTERACTIVE =
  "hover:border-l-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 disabled:cursor-default disabled:opacity-60";
const BG_DEFAULT =
  "border-slate-200 bg-white dark:border-slate-700 dark:bg-arti-panel";
const BG_MUTED =
  "border-slate-100 bg-slate-50 dark:border-slate-700 dark:bg-arti-background";

/**
 * Shared accent-card shell used across the dashboard for status-aware cards.
 *
 * Renders a `<button>` when `onClick` is provided, otherwise a `<div>`.
 * Pair with `getStatusAccent` / `getStatusDot` from `statusColors` to
 * derive the `accent` prop from a status string.
 */
export const AccentCard: FC<AccentCardProps> = ({
  accent = "border-l-gray-400",
  onClick,
  disabled,
  title,
  variant = "default",
  className,
  children,
}) => {
  const bg = variant === "muted" ? BG_MUTED : BG_DEFAULT;
  const interactive = onClick ? INTERACTIVE : "";
  const cursor = onClick && !disabled ? "cursor-pointer" : "";

  const classes = [
    "w-full px-2.5 py-1.5",
    BASE,
    bg,
    interactive,
    cursor,
    accent,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  if (onClick) {
    return (
      <button
        type="button"
        className={classes}
        onClick={onClick}
        disabled={disabled}
        title={title}
      >
        {children}
      </button>
    );
  }

  return (
    <div className={classes} title={title}>
      {children}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Helpers for common inner patterns                                  */
/* ------------------------------------------------------------------ */

/** Title text style — monospace bold. */
export const CARD_TITLE_CLASS =
  "min-w-0 flex-1 truncate font-mono text-xs font-bold text-slate-800 dark:text-slate-100";

/** Description text style — monospace muted, single-line clamp. */
export const CARD_DESC_CLASS =
  "mt-0.5 line-clamp-1 pl-3 font-mono text-[11px] leading-tight text-slate-500 dark:text-slate-400";

/** Metadata / footer text style — monospace muted. */
export const CARD_META_CLASS =
  "font-mono text-[10px] text-slate-400 dark:text-slate-500";

/** Status label style — monospace uppercase tracking. */
export const CARD_STATUS_CLASS =
  "flex-shrink-0 font-mono text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500";

/**
 * Convenience: derive pulse boolean from a raw status string.
 * Re-exported from statusColors for co-location with card components.
 */
export { isActiveStatus };
